/**
 * Formatters Utility
 * Format currency, numbers, dates, percentages for display
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, these are display-only formatters.
 * NO business logic or calculations.
 */

/**
 * Format currency (Indian Rupees)
 */
export function formatCurrency(amount: number, options: { showSign?: boolean; decimals?: number } = {}): string {
  const { showSign = false, decimals = 2 } = options;

  const sign = showSign && amount >= 0 ? '+' : '';
  const formatted = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Math.abs(amount));

  return `${sign}${formatted}`;
}

/**
 * Format number with Indian locale
 */
export function formatNumber(value: number, decimals: number = 2): string {
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format percentage
 */
export function formatPercentage(value: number | undefined | null, decimals: number = 2, showSign: boolean = true): string {
  if (value === undefined || value === null || isNaN(value)) {
    return '0.00%';
  }
  const sign = showSign && value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals)}%`;
}

/**
 * Format date/time
 */
export function formatDateTime(date: string | Date, options: { includeTime?: boolean } = {}): string {
  const { includeTime = false } = options;
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (includeTime) {
    return new Intl.DateTimeFormat('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(dateObj);
  }

  return new Intl.DateTimeFormat('en-IN', {
    dateStyle: 'medium',
  }).format(dateObj);
}

/**
 * Format relative time (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return 'Just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  } else {
    return formatDateTime(dateObj);
  }
}

/**
 * Format large numbers (e.g., 1.5L, 2.5Cr)
 */
export function formatLargeNumber(value: number): string {
  if (value >= 10000000) {
    return `${(value / 10000000).toFixed(2)}Cr`;
  } else if (value >= 100000) {
    return `${(value / 100000).toFixed(2)}L`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(2)}K`;
  }
  return formatNumber(value, 0);
}

